// O3 — Goals / OKRs page.
// Lists all visible goals with status badge + progress bar.
// Create form inline at top. Click a goal to open a detail panel with
// linked projects/tasks, link management and edit/delete controls.

import { useEffect, useState } from "preact/hooks";
import { Plus, Target, Trash2, Unlink, X } from "lucide-preact";
import { Layout } from "../components/Layout";
import { Modal } from "../components/Modal";
import { PageShell } from "../components/ui/PageShell";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { useMe } from "../lib/auth";
import { useProjects } from "../store/projects";
import { formatDateShort, softPillStyle } from "../lib/format";
import {
  listGoals,
  getGoal,
  createGoal,
  updateGoal,
  deleteGoal,
  addGoalLink,
  removeGoalLink,
  type ApiGoal,
  type ApiGoalDetail,
  type GoalStatus,
} from "../lib/api";

import { Button } from "../components/ui/Button";

// ---------- status helpers ----------

export const GOAL_STATUS_LABEL: Record<GoalStatus, string> = {
  on_track: "Im Plan",
  at_risk: "Gefährdet",
  off_track: "Abweichend",
  done: "Abgeschlossen",
};

/**
 * Returns a CSS background-color value for a goal status badge.
 * Pure function — easily unit-testable without DOM.
 */
export function goalStatusColor(status: GoalStatus): string {
  switch (status) {
    case "on_track":
      return "var(--color-status-done)";
    case "at_risk":
      return "var(--color-prio-high)";
    case "off_track":
      return "var(--color-prio-urgent)";
    case "done":
      return "var(--color-status-in-progress)";
  }
}

/**
 * Clamps progress to [0, 100] and formats it as "N%".
 * Pure — easily unit-testable.
 */
export function formatGoalProgress(raw: number): string {
  const clamped = Math.max(0, Math.min(100, Math.round(raw)));
  return `${clamped}%`;
}

// ---------- sub-components ----------

function StatusBadge({ status }: { status: GoalStatus }): preact.JSX.Element {
  return (
    <span
      class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-label font-semibold shrink-0"
      style={softPillStyle(goalStatusColor(status))}
    >
      <span
        class="inline-block size-1.5 rounded-full shrink-0"
        style={{ background: goalStatusColor(status) }}
        aria-hidden="true"
      />
      {GOAL_STATUS_LABEL[status]}
    </span>
  );
}

function ProgressBar({ value }: { value: number }): preact.JSX.Element {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      class="w-full h-1.5 rounded-full bg-[color:var(--color-surface-container-low)] overflow-hidden"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Fortschritt: ${pct}%`}
    >
      <div
        class="h-full rounded-full transition-[width] duration-300"
        style={{ width: `${pct}%`, background: "var(--color-primary)" }}
      />
    </div>
  );
}

// ---------- create form ----------

interface CreateFormProps {
  onCreated: (g: ApiGoal) => void;
}

function CreateForm({ onCreated }: CreateFormProps): preact.JSX.Element {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<GoalStatus>("on_track");
  const [targetDate, setTargetDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reset(): void {
    setTitle("");
    setDescription("");
    setStatus("on_track");
    setTargetDate("");
    setErr(null);
  }

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const g = await createGoal({
        title: title.trim(),
        description: description.trim() || null,
        status,
        target_date: targetDate || null,
      });
      onCreated(g);
      reset();
      setOpen(false);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <Button
        variant="primary"
        size="lg"
        type="button"
        onClick={() => setOpen(true)}
      >
        <Plus size={16} aria-hidden="true" />
        Neues Ziel
      </Button>
    );
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4 flex flex-col gap-3 mb-4"
      aria-label="Neues Ziel erstellen"
    >
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-sm font-semibold text-[color:var(--color-on-surface)]">Neues Ziel</h3>
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          aria-label="Formular schließen"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="goal-title">
          Titel *
        </label>
        <input
          id="goal-title"
          type="text"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder="Ziel benennen …"
          required
          maxLength={200}
          class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[36px]"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="goal-desc">
          Beschreibung
        </label>
        <textarea
          id="goal-desc"
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          maxLength={10000}
          class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] resize-none"
        />
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="goal-status">
            Status
          </label>
          <select
            id="goal-status"
            value={status}
            onChange={(e) => setStatus((e.target as HTMLSelectElement).value as GoalStatus)}
            class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[36px]"
          >
            {(["on_track", "at_risk", "off_track", "done"] as GoalStatus[]).map((s) => (
              <option key={s} value={s}>{GOAL_STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="goal-date">
            Zieldatum
          </label>
          <input
            id="goal-date"
            type="date"
            value={targetDate}
            onInput={(e) => setTargetDate((e.target as HTMLInputElement).value)}
            class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[36px]"
          />
        </div>
      </div>
      {err && (
        <p role="alert" class="text-xs text-[color:var(--color-error)]">{err}</p>
      )}
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[32px]"
        >
          Abbrechen
        </button>
        <Button
          variant="primary"
          size="md"
          type="submit"
          disabled={saving || !title.trim()}
        >
          {saving ? "Erstellt…" : "Erstellen"}
        </Button>
      </div>
    </form>
  );
}

// ---------- detail panel ----------

interface DetailPanelProps {
  goalId: number;
  currentUserId: number;
  isAdmin: boolean;
  onClose: () => void;
  onDeleted: (id: number) => void;
  onUpdated: (g: ApiGoal) => void;
}

function DetailPanel({ goalId, currentUserId, isAdmin, onClose, onDeleted, onUpdated }: DetailPanelProps): preact.JSX.Element {
  const [detail, setDetail] = useState<ApiGoalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // edit mode
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editStatus, setEditStatus] = useState<GoalStatus>("on_track");
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);

  // link panel
  const [linkMode, setLinkMode] = useState<"none" | "project" | "task">("none");
  const [linkProjectId, setLinkProjectId] = useState("");
  const [linkTaskId, setLinkTaskId] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkErr, setLinkErr] = useState<string | null>(null);

  const projects = useProjects();

  function loadDetail(): void {
    setLoading(true);
    setErr(null);
    void getGoal(goalId).then(
      (d) => { setDetail(d); setLoading(false); },
      (e) => { setErr(e instanceof Error ? e.message : "Fehler"); setLoading(false); }
    );
  }

  useEffect(() => {
    loadDetail();
  }, [goalId]);

  function startEdit(): void {
    if (!detail) return;
    setEditTitle(detail.title);
    setEditDesc(detail.description ?? "");
    setEditStatus(detail.status);
    setEditDate(detail.target_date ?? "");
    setEditErr(null);
    setEditing(true);
  }

  async function handleSaveEdit(e: Event): Promise<void> {
    e.preventDefault();
    if (!detail) return;
    setSaving(true);
    setEditErr(null);
    try {
      const updated = await updateGoal(detail.id, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
        status: editStatus,
        target_date: editDate || null,
      });
      onUpdated(updated);
      setDetail({ ...detail, ...updated });
      setEditing(false);
    } catch (ex) {
      setEditErr(ex instanceof Error ? ex.message : "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!detail) return;
    if (!window.confirm(`Ziel "${detail.title}" wirklich löschen?`)) return;
    try {
      await deleteGoal(detail.id);
      onDeleted(detail.id);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Fehler beim Löschen");
    }
  }

  async function handleAddLink(e: Event): Promise<void> {
    e.preventDefault();
    if (!detail) return;
    setLinkSaving(true);
    setLinkErr(null);
    try {
      const body: { project_id?: number; task_id?: number } = {};
      if (linkMode === "project" && linkProjectId) {
        body.project_id = parseInt(linkProjectId, 10);
      } else if (linkMode === "task" && linkTaskId) {
        body.task_id = parseInt(linkTaskId, 10);
      } else {
        setLinkErr("Bitte einen Wert eingeben.");
        setLinkSaving(false);
        return;
      }
      await addGoalLink(detail.id, body);
      setLinkMode("none");
      setLinkProjectId("");
      setLinkTaskId("");
      loadDetail();
    } catch (ex) {
      setLinkErr(ex instanceof Error ? ex.message : "Fehler beim Verknüpfen");
    } finally {
      setLinkSaving(false);
    }
  }

  async function handleRemoveLink(linkId: number): Promise<void> {
    if (!detail) return;
    try {
      await removeGoalLink(detail.id, linkId);
      loadDetail();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Fehler beim Entfernen");
    }
  }

  const isOwner = isAdmin || (detail?.owner_id === currentUserId);

  // Statt eines eigenen `fixed inset-0`-Overlays (abweichende Abdunklung
  // `/50` ohne Blur-Radius) laeuft der Dialog jetzt ueber `components/Modal.tsx`
  // — dieselbe Abdunklung wie die restlichen 17 Modal-Nutzer der App, plus
  // Fokus-Falle und Escape/Backdrop-Handling, die dieses Panel vorher nicht hatte.
  return (
    <Modal title={detail?.title ?? (loading ? "Lädt…" : "Ziel-Details")} onClose={onClose} size="md">
      <div class="flex flex-col gap-4">
        {loading && (
          <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>
        )}
        {!loading && err && (
          <p role="alert" class="text-sm text-[color:var(--color-error)]">{err}</p>
        )}
        {!loading && detail && !editing && (
          <>
            <div class="flex items-center gap-2 flex-wrap">
              <StatusBadge status={detail.status} />
              {detail.target_date && (
                <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                  Zieldatum: {formatDateShort(detail.target_date)}
                </span>
              )}
            </div>
            <div>
              <p class="text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1">
                Fortschritt: {formatGoalProgress(detail.progress)}
              </p>
              <ProgressBar value={detail.progress} />
            </div>
            {detail.description && (
              <p class="text-sm text-[color:var(--color-on-surface-variant)]">{detail.description}</p>
            )}

            {/* Links section */}
            <div>
              <h3 class="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-on-surface-variant)] mb-2">
                Verknüpfungen
              </h3>
              {detail.links.length === 0 ? (
                <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Keine Verknüpfungen.</p>
              ) : (
                <ul class="flex flex-col gap-1">
                  {detail.links.map((lk) => (
                    <li key={lk.id} class="flex items-center justify-between gap-2 rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1.5 text-xs text-[color:var(--color-on-surface)]">
                      <span>
                        {lk.project_id != null
                          ? `Projekt #${lk.project_id}`
                          : `Aufgabe #${lk.task_id}`}
                      </span>
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => void handleRemoveLink(lk.id)}
                          class="rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-error)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label="Verknüpfung entfernen"
                        >
                          <Unlink size={14} aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* Add link form */}
              {isOwner && linkMode === "none" && (
                <div class="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setLinkMode("project"); setLinkErr(null); }}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[28px]"
                  >
                    + Projekt verknüpfen
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLinkMode("task"); setLinkErr(null); }}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[28px]"
                  >
                    + Aufgabe verknüpfen
                  </button>
                </div>
              )}
              {isOwner && linkMode === "project" && (
                <form
                  onSubmit={(e) => void handleAddLink(e)}
                  class="mt-2 flex flex-col gap-2"
                  aria-label="Projekt verknüpfen"
                >
                  <select
                    value={linkProjectId}
                    onChange={(e) => setLinkProjectId((e.target as HTMLSelectElement).value)}
                    class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[32px]"
                    aria-label="Projekt auswählen"
                  >
                    <option value="">Projekt wählen …</option>
                    {projects.items.map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))}
                  </select>
                  {linkErr && <p role="alert" class="text-xs text-[color:var(--color-error)]">{linkErr}</p>}
                  <div class="flex gap-2">
                    <Button
                      variant="primary"
                      size="xs"
                      type="submit"
                      disabled={linkSaving || !linkProjectId}
                    >
                      {linkSaving ? "…" : "Verknüpfen"}
                    </Button>
                    <button type="button" onClick={() => setLinkMode("none")} class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[28px]">
                      Abbrechen
                    </button>
                  </div>
                </form>
              )}
              {isOwner && linkMode === "task" && (
                <form
                  onSubmit={(e) => void handleAddLink(e)}
                  class="mt-2 flex flex-col gap-2"
                  aria-label="Aufgabe verknüpfen"
                >
                  <input
                    type="number"
                    min={1}
                    value={linkTaskId}
                    onInput={(e) => setLinkTaskId((e.target as HTMLInputElement).value)}
                    placeholder="Aufgaben-ID …"
                    class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[32px]"
                  />
                  {linkErr && <p role="alert" class="text-xs text-[color:var(--color-error)]">{linkErr}</p>}
                  <div class="flex gap-2">
                    <Button
                      variant="primary"
                      size="xs"
                      type="submit"
                      disabled={linkSaving || !linkTaskId}
                    >
                      {linkSaving ? "…" : "Verknüpfen"}
                    </Button>
                    <button type="button" onClick={() => setLinkMode("none")} class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[28px]">
                      Abbrechen
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Owner actions */}
            {isOwner && (
              <div class="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--color-outline-variant)]">
                <button
                  type="button"
                  onClick={startEdit}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[32px]"
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  class="inline-flex items-center gap-1 rounded border border-[color:var(--color-error-container)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)]/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] min-h-[32px]"
                >
                  <Trash2 size={14} aria-hidden="true" />
                  Löschen
                </button>
              </div>
            )}
          </>
        )}

        {/* Edit mode */}
        {!loading && detail && editing && (
          <form onSubmit={(e) => void handleSaveEdit(e)} class="flex flex-col gap-3">
            <h2 class="text-base font-semibold text-[color:var(--color-on-surface)]">Ziel bearbeiten</h2>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="edit-goal-title">Titel *</label>
              <input
                id="edit-goal-title"
                type="text"
                value={editTitle}
                onInput={(e) => setEditTitle((e.target as HTMLInputElement).value)}
                required
                maxLength={200}
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[36px]"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="edit-goal-desc">Beschreibung</label>
              <textarea
                id="edit-goal-desc"
                value={editDesc}
                onInput={(e) => setEditDesc((e.target as HTMLTextAreaElement).value)}
                rows={2}
                maxLength={10000}
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] resize-none"
              />
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="edit-goal-status">Status</label>
                <select
                  id="edit-goal-status"
                  value={editStatus}
                  onChange={(e) => setEditStatus((e.target as HTMLSelectElement).value as GoalStatus)}
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[36px]"
                >
                  {(["on_track", "at_risk", "off_track", "done"] as GoalStatus[]).map((s) => (
                    <option key={s} value={s}>{GOAL_STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-[color:var(--color-on-surface-variant)]" for="edit-goal-date">Zieldatum</label>
                <input
                  id="edit-goal-date"
                  type="date"
                  value={editDate}
                  onInput={(e) => setEditDate((e.target as HTMLInputElement).value)}
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[36px]"
                />
              </div>
            </div>
            {editErr && <p role="alert" class="text-xs text-[color:var(--color-error)]">{editErr}</p>}
            <div class="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setEditing(false)}
                class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[32px]"
              >
                Abbrechen
              </button>
              <Button
                variant="primary"
                size="md"
                type="submit"
                disabled={saving || !editTitle.trim()}
              >
                {saving ? "Speichert…" : "Speichern"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// ---------- goal card ----------

function GoalCard({ goal, onClick }: { goal: ApiGoal; onClick: () => void }): preact.JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        class="w-full text-left rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-3 hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
      >
        <div class="flex items-start gap-3">
          <Target size={16} aria-hidden="true" class="mt-0.5 shrink-0 text-[color:var(--color-primary)]" />
          <div class="flex-1 min-w-0 flex flex-col gap-1.5">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-medium text-[color:var(--color-on-surface)] flex-1 min-w-0 truncate">
                {goal.title}
              </span>
              <StatusBadge status={goal.status} />
            </div>
            {goal.target_date && (
              <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                Ziel bis {formatDateShort(goal.target_date)}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

// ---------- main page ----------

export function Goals(): preact.JSX.Element {
  const me = useMe();
  const [goals, setGoals] = useState<ApiGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listGoals().then(
      (rows) => { if (!cancelled) { setGoals(rows); setLoading(false); } },
      (e) => { if (!cancelled) { setError(e instanceof Error ? e.message : "Fehler"); setLoading(false); } }
    );
    return () => { cancelled = true; };
  }, [reloadKey]);

  function handleCreated(g: ApiGoal): void {
    setGoals((prev) => [g, ...prev]);
  }

  function handleDeleted(id: number): void {
    setGoals((prev) => prev.filter((g) => g.id !== id));
    setSelectedId(null);
  }

  function handleUpdated(updated: ApiGoal): void {
    setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  }

  usePageHeader({
    title: requireLabel("/goals"),
    subtitle: "Strategische Ziele und OKRs deiner Organisation.",
  });

  return (
    <Layout>
      {/* default (1440px): Ziele sind eine Listen-/Rasterflaeche mit Detail-Dialog,
          keine reine Lese-/Formularseite — und hatte vorher gar kein `max-w`. */}
      <PageShell width="default">
        {!loading && !error && (
          <div class="mb-6 flex justify-end">
            <CreateForm onCreated={handleCreated} />
          </div>
        )}

        {loading && (
          <div class="space-y-2">
            <LoadingSkeleton height={64} />
            <LoadingSkeleton height={64} />
            <LoadingSkeleton height={64} />
          </div>
        )}

        {!loading && error && (
          <div
            role="alert"
            class="flex flex-wrap items-center gap-3 rounded border border-[color:var(--color-error-container)] bg-[color:var(--color-error-container)] px-3 py-2 text-sm text-[color:var(--color-on-error-container)]"
          >
            <span class="flex-1 min-w-0">Ziele konnten nicht geladen werden: {error}</span>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              class="shrink-0 rounded border border-[color:var(--color-on-error-container)]/40 px-2 py-1 text-xs font-medium text-[color:var(--color-on-error-container)] hover:bg-[color:var(--color-on-error-container)]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-on-error-container)]/50"
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {!loading && !error && goals.length === 0 && (
          <EmptyState
            icon={Target}
            title="Noch keine Ziele definiert"
            description='Klicke auf "Neues Ziel", um das erste Ziel zu erstellen.'
          />
        )}

        {!loading && !error && goals.length > 0 && (
          <ul class="flex flex-col gap-2" role="list" aria-label="Ziele">
            {goals.map((g) => (
              <GoalCard
                key={g.id}
                goal={g}
                onClick={() => setSelectedId(g.id)}
              />
            ))}
          </ul>
        )}
      </PageShell>

      {selectedId != null && (
        <DetailPanel
          goalId={selectedId}
          currentUserId={me.user?.id ?? -1}
          isAdmin={me.user?.is_admin ?? false}
          onClose={() => setSelectedId(null)}
          onDeleted={handleDeleted}
          onUpdated={handleUpdated}
        />
      )}
    </Layout>
  );
}
